import { createSelector } from 'reselect';
import path from 'ramda/es/path';
import pipe from 'ramda/es/pipe';
import pick from 'ramda/es/pick';
import merge from 'ramda/es/merge';
import has from 'ramda/es/has';
import __ from 'ramda/es//__';

import { TARIFF_CONSTRUCTOR, TARIFF_MONTHLY, TARIFF_BUNDLE, TARIFF_EXTERNAL_SYSTEM } from 'app/common/constants/tariffs';
import composePackTariff from 'app/components/Tariff/composePackTariff';
import selectActiveServices from 'app/components/Tariff/selectActiveServices';


const isPauseAfterPeriod = path(['month_daily_params', 'pause_after_period']);
const isMoneyReturn = path(['month_daily_params', 'money_return']);

/**
* Тариф,
* берется из props, если там нет, то из подписки
*/
const createSubDataSelector = () => createSelector(
  (state, { subId }) => ((subId && state.apps.subs[subId]) || null),
  sub => sub
);

export const tariffBySubId = (state, { subId, tariff }) => (tariff || pipe(
  s => path(['apps', 'subs', subId], s) || {},
  pick(['tariff_bundle_id', 'tariff_id']),
  ({ tariff_bundle_id: tbId, tariff_id: tId }) => (
    (tbId && path(['apps', 'tariffBundles', tbId], state))
    || (tId && path(['apps', 'tariffs', tId], state))
  )
)(state));

const createTariffDataSelector = () => createSelector(
  [
    tariffBySubId,
    (state, { subId }) => (subId && path(['apps', 'subs', subId, 'modified'], state)) || {},
    (state, { service }) => service,
    state => state.apps.tariffs,
    selectActiveServices,
  ],
  (tariff, modified, service, allTariffs, activeServices) => pipe(
    tf => (has('tariff_ids', tf)
      ? composePackTariff(allTariffs, service, activeServices)(tf)
      : tf
    ),
    merge(__, modified)
  )(tariff)
);

// сноски
const createFootnotesDataSelector = (selectTariffData, selectSubData) => createSelector(
  [
    selectTariffData,
    selectSubData,
    (_, { t }) => t,
  ],
  (tariff, sub, t) => (tariff.kind === TARIFF_EXTERNAL_SYSTEM ? {} : {
    cost: (tariff.kind === TARIFF_MONTHLY || tariff.kind === TARIFF_BUNDLE)
      && t('note_cost'),

    writeoff: tariff.kind === TARIFF_CONSTRUCTOR && (tariff.lk_cost || tariff.mobile_cost)
      && t('note_writeoff'),

    sms: tariff.kind !== TARIFF_CONSTRUCTOR && tariff.sms_enabled && tariff.sms_limit && tariff.sms_cost
      // && t('note_sms', { sms_limit: tariff.sms_limit, sms_cost: tariff.sms_cost }),
      && t('note_sms', { sms_limit: tariff.sms_limit, sms_cost: ' ' }),

    smspack: tariff.kind === TARIFF_CONSTRUCTOR && tariff.sms_cost
      // && t('note_smspack', { sms_cost: tariff.sms_cost }),
      && t('note_smspack', { sms_cost: ' ' }),

    monthDailyPause: (tariff.kind === TARIFF_MONTHLY || tariff.kind === TARIFF_BUNDLE)
      && (isPauseAfterPeriod(tariff)
        ? t('note_month_daily_pause1')
        : t('note_month_daily_pause2')),

    pause: !!sub && t('note_pause'),

    stop: !!sub && (isMoneyReturn(tariff)
      ? t('note_stop1')
      : t('note_stop2')
    ),

    paytoday: sub && sub.pay_ready
      && t('note_paytoday'),
  })
);

const createTariffSelector = () => {
  const selectSubData = createSubDataSelector();
  const selectTariffData = createTariffDataSelector();
  const selectFootnotesData = createFootnotesDataSelector(selectTariffData, selectSubData);
  return createSelector(
    [
      selectSubData,
      selectTariffData,
      selectFootnotesData,
    ],
    (sub, tariff, footnotesData) => {
      const footnotesKeys = (
        // последовательность сносок соотвествует последовательности в массиве
        // РАСПОЛОЖЕНИЕ:
        // cost -- стоимость тарифа (выше всех)
        // writeoff -- пункты списка услуг (Личный кабиинет)
        // sms --
        // smspack -- справа над индикатором смс и слева над пунктом "SMS-уведомления" в тарифе-конструкторе
        // monthDailyPause -- справа над инфой "Оплачено до" для тарифа month_daily
        // pause -- над кнопкой паузы тарифа
        // paytoday -- списание?
        ['cost', 'writeoff', 'sms', 'smspack', 'monthDailyPause', 'paytoday', 'pause', 'stop']
          .filter(item => !!footnotesData[item]));

      const footnotes = footnotesKeys.reduce((acc, curr, i) => {
        acc[curr] = {};
        acc[curr].text = footnotesData[curr];
        acc[curr].index = i + 1;
        return acc;
      }, {});
      return ({
        sub,
        tariff,
        footnotes,
        footnotesKeys,
      });
    }
  );
};

export default createTariffSelector;
